<template>
  <div>
    <!-- 设备收入排行 -->
    <div class="card mb-3">
      <div class="card-header pb-0">
        <h6>设备收入排行</h6>
        <p class="text-xs text-secondary">全平台设备按收入排名（Top 20），点击「流水」查看订单明细</p>
      </div>
      <div class="card-body px-0 pt-0 pb-2">
        <div v-if="loading" class="text-center text-secondary text-xs py-4">加载中...</div>
        <div v-else-if="deviceRanking.length === 0" class="text-center text-secondary text-xs py-4">暂无数据</div>
        <div v-else class="table-responsive p-0">
          <table class="table align-items-center mb-0">
            <thead>
              <tr>
                <th class="text-xs text-secondary font-weight-bolder opacity-7 text-center" style="width:50px">#</th>
                <th class="text-xs text-secondary font-weight-bolder opacity-7">设备ID</th>
                <th class="text-xs text-secondary font-weight-bolder opacity-7">商户</th>
                <th class="text-xs text-secondary font-weight-bolder opacity-7 text-end">总收入</th>
                <th class="text-xs text-secondary font-weight-bolder opacity-7 text-end">净收入</th>
                <th class="text-xs text-secondary font-weight-bolder opacity-7">占比</th>
                <th class="text-xs text-secondary opacity-7"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in deviceRanking" :key="item.board_id">
                <td class="text-center text-xs font-weight-bold">{{ index + 1 }}</td>
                <td class="text-xs font-monospace">{{ item.board_id }}</td>
                <td class="text-xs">{{ item.deliver_id }}<span class="text-secondary" v-if="merchantName(item.deliver_id)"> ({{ merchantName(item.deliver_id) }})</span></td>
                <td class="text-xs text-end font-weight-bold">¥{{ formatMoney(item.sum_fee) }}</td>
                <td class="text-xs text-end text-success">¥{{ formatMoney(item.net_fee) }}</td>
                <td>
                  <div class="d-flex align-items-center">
                    <span class="me-2 text-xs font-weight-bold">{{ item.percentage.toFixed(1) }}%</span>
                    <div style="width:80px">
                      <div class="progress" style="height:6px">
                        <div class="progress-bar bg-info" :style="{ width: Math.min(item.percentage, 100) + '%' }"></div>
                      </div>
                    </div>
                  </div>
                </td>
                <td class="align-middle">
                  <a href="javascript:;" class="text-secondary font-weight-bold text-xs" @click="showOrderDetail(item.board_id)">流水</a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="row">
      <!-- 补偿出货统计 -->
      <div class="col-lg-6 mb-3">
        <div class="card h-100">
          <div class="card-header pb-0">
            <h6>补偿出货统计</h6>
            <p class="text-xs text-secondary">哪些设备补偿率较高</p>
          </div>
          <div class="card-body px-0 pt-0 pb-2">
            <div v-if="retryLoading" class="text-center text-secondary text-xs py-4">加载中...</div>
            <div v-else-if="retryStats.length === 0" class="text-center text-secondary text-xs py-4">暂无数据</div>
            <div v-else class="table-responsive p-0">
              <table class="table table-sm align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-xs text-secondary">设备ID</th>
                    <th class="text-xs text-secondary text-end">重试数</th>
                    <th class="text-xs text-secondary text-end">成功</th>
                    <th class="text-xs text-secondary text-end">失败</th>
                    <th class="text-xs text-secondary text-end">失败率</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in retryStats.slice(0, 10)" :key="item.board_id">
                    <td class="text-xs font-monospace">{{ item.board_id }}</td>
                    <td class="text-xs text-end">{{ item.retry_count }}</td>
                    <td class="text-xs text-end text-success">{{ item.success_count }}</td>
                    <td class="text-xs text-end text-danger">{{ item.fail_count }}</td>
                    <td class="text-xs text-end">
                      <span :class="failRateClass(item)">{{ failRate(item) }}%</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- 设备销售明细 -->
      <div class="col-lg-6 mb-3">
        <div class="card h-100">
          <div class="card-header pb-0">
            <h6>设备销售分析</h6>
            <p class="text-xs text-secondary">货道维度销售统计</p>
          </div>
          <div class="card-body px-0 pt-0 pb-2">
            <div v-if="salesLoading" class="text-center text-secondary text-xs py-4">加载中...</div>
            <div v-else-if="deviceSales.length === 0" class="text-center text-secondary text-xs py-4">暂无数据</div>
            <div v-else class="table-responsive p-0">
              <table class="table table-sm align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-xs text-secondary">设备序列号</th>
                    <th class="text-xs text-secondary text-end">订单数</th>
                    <th class="text-xs text-secondary text-end">总金额</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in deviceSales.slice(0, 10)" :key="idx">
                    <td class="text-xs font-monospace">{{ item.device_serial || item.board_id || '-' }}</td>
                    <td class="text-xs text-end">{{ item.order_count || 0 }}</td>
                    <td class="text-xs text-end">¥{{ formatMoney(item.total_fee || item.sum_fee || 0) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 设备订单流水弹窗 -->
    <div v-if="orderDetailVisible" class="modal d-block" tabindex="-1" style="background:rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-xl modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">设备 {{ orderDetailBoard }} 订单流水</h5>
            <button type="button" class="btn-close" @click="closeOrderDetail"></button>
          </div>
          <div class="modal-body">
            <div v-if="orderLoading" class="text-center text-secondary text-xs py-4">加载中...</div>
            <div v-else-if="orderRecords.length === 0" class="text-center text-secondary text-xs py-4">暂无订单数据</div>
            <div v-else class="table-responsive">
              <table class="table table-sm align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-xs text-secondary">订单号</th>
                    <th class="text-xs text-secondary">设备ID</th>
                    <th class="text-xs text-secondary text-end">金额</th>
                    <th class="text-xs text-secondary text-center">状态</th>
                    <th class="text-xs text-secondary">微信交易号</th>
                    <th class="text-xs text-secondary text-end">时间</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in orderRecords" :key="item.order_no">
                    <td class="text-xs font-monospace">{{ item.order_no }}</td>
                    <td class="text-xs font-monospace">{{ item.board_id }}</td>
                    <td class="text-xs text-end">¥{{ formatMoney(item.fee) }}</td>
                    <td class="text-center">
                      <span :class="orderStateClass(item.state)" class="badge text-xs">{{ orderStateText(item.state) }}</span>
                    </td>
                    <td class="text-xs font-monospace">{{ item.wx_transation_id || '-' }}</td>
                    <td class="text-xs text-end">{{ formatDateTime(item.created_date) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <div class="me-auto">
              <span class="text-xs text-secondary me-3">共 {{ orderRecords.length }} 条记录</span>
              <span class="text-xs text-secondary me-3">|</span>
              <span class="text-xs text-secondary me-2">总金额: <span class="font-weight-bold text-primary">¥{{ formatMoney(orderSummary.totalFee) }}</span></span>
              <span class="text-xs text-secondary" v-if="orderSummary.refundFee > 0">（含退款: ¥{{ formatMoney(orderSummary.refundFee) }}）</span>
            </div>
            <button type="button" class="btn btn-sm btn-secondary" @click="closeOrderDetail">关闭</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getSumReports, getRetryStats, getDeviceSalesStats, getMerchantOrders } from "@/api/dashboard";
import { getmerchlist } from "@/api/merch.js";

export default {
  name: "DeviceAnalysis",
  props: {
    days: { type: Number, default: 30 },
    deliverId: { type: Number, default: 0 },
    startDate: { type: Number, required: true },
    endDate: { type: Number, required: true },
  },
  data() {
    return {
      deviceRanking: [],
      retryStats: [],
      deviceSales: [],
      merchantMap: {},
      loading: false,
      retryLoading: false,
      salesLoading: false,
      orderDetailVisible: false,
      orderDetailBoard: "",
      orderRecords: [],
      orderLoading: false,
    };
  },
  computed: {
    orderSummary() {
      let totalFee = 0;
      let refundFee = 0;
      this.orderRecords.forEach((r) => {
        totalFee += r.fee || 0;
        if (r.state === 2) refundFee += r.fee || 0;
      });
      return { totalFee, refundFee };
    },
  },
  mounted() {
    this.fetchAll();
  },
  methods: {
    formatMoney(fen) {
      if (!fen) return "0.00";
      return (fen / 100).toFixed(2);
    },
    formatDateTime(ts) {
      if (!ts) return "-";
      const d = new Date(ts * 1000);
      return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    },
    failRate(item) {
      if (!item.retry_count || item.retry_count === 0) return 0;
      return ((item.fail_count / item.retry_count) * 100).toFixed(1);
    },
    failRateClass(item) {
      const rate = parseFloat(this.failRate(item));
      if (rate > 20) return "text-danger font-weight-bold";
      if (rate > 5) return "text-warning";
      return "text-success";
    },
    orderStateText(state) {
      if (state === 1) return "成功";
      if (state === 2) return "退款";
      if (state === 0) return "待处理";
      return "未知";
    },
    orderStateClass(state) {
      if (state === 1) return "bg-success";
      if (state === 2) return "bg-danger";
      if (state === 0) return "bg-warning text-dark";
      return "bg-secondary";
    },
    async showOrderDetail(boardId) {
      this.orderDetailBoard = boardId;
      this.orderDetailVisible = true;
      this.orderLoading = true;
      try {
        const params = { start_date: this.startDate, end_date: this.endDate, page: 1, page_size: 10000, board_id: boardId };
        if (this.deliverId > 0) params.deliver_id = this.deliverId;
        const resp = await getMerchantOrders(params);
        if (resp.code === 0 && resp.desc) {
          let records = [];
          if (Array.isArray(resp.desc)) {
            records = resp.desc;
          } else if (resp.desc.records) {
            records = resp.desc.records;
          }
          this.orderRecords = records;
        }
      } catch (err) {
        console.error("获取设备订单流水失败:", err);
      } finally {
        this.orderLoading = false;
      }
    },
    closeOrderDetail() {
      this.orderDetailVisible = false;
      this.orderDetailBoard = "";
      this.orderRecords = [];
    },
    async fetchAll() {
      this.loadMerchantCache();
      await Promise.all([
        this.fetchDeviceRanking(),
        this.fetchRetryStats(),
        this.fetchDeviceSales(),
      ]);
    },
    loadMerchantCache() {
      const cached = localStorage.getItem("cs_merchant_map");
      if (cached) {
        try {
          this.merchantMap = JSON.parse(cached);
          return;
        } catch (e) {
          // 缓存解析失败，继续拉取
        }
      }
      this.fetchAllMerchants();
    },
    async fetchAllMerchants() {
      try {
        const pageSize = 200;
        let page = 1;
        let totalPages = 1;
        const map = {};
        while (page <= totalPages) {
          const resp = await getmerchlist({ page, page_size: pageSize });
          if (resp.code !== 0 || !resp.desc) break;
          const delivers = resp.desc.delivers || [];
          delivers.forEach((d) => {
            map[d.id] = d.nick_name || d.name || "";
          });
          totalPages = resp.desc.total_pages || 1;
          page++;
        }
        this.merchantMap = map;
        localStorage.setItem("cs_merchant_map", JSON.stringify(map));
      } catch (err) {
        console.error("获取商户列表失败:", err);
      }
    },
    merchantName(deliverId) {
      return this.merchantMap[deliverId] || "";
    },
    async fetchDeviceRanking() {
      this.loading = true;
      try {
        const params = { start_date: this.startDate, end_date: this.endDate };
        if (this.deliverId > 0) params.deliver_id = this.deliverId;
        const resp = await getSumReports(params);
        if (resp.code === 0 && Array.isArray(resp.desc)) {
          const boardMap = {};
          let totalFee = 0;
          resp.desc.forEach((item) => {
            const key = item.board_id + "_" + item.deliver_id;
            if (!boardMap[key]) {
              boardMap[key] = { board_id: item.board_id, deliver_id: item.deliver_id, sum_fee: 0, net_fee: 0 };
            }
            boardMap[key].sum_fee += item.sum_fee || 0;
            boardMap[key].net_fee += item.net_fee || 0;
            totalFee += item.sum_fee || 0;
          });
          this.deviceRanking = Object.values(boardMap)
            .sort((a, b) => b.sum_fee - a.sum_fee)
            .slice(0, 20)
            .map((item) => ({
              ...item,
              percentage: totalFee > 0 ? (item.sum_fee / totalFee) * 100 : 0,
            }));
        }
      } catch (err) {
        console.error("获取设备排行失败:", err);
      } finally {
        this.loading = false;
      }
    },
    async fetchRetryStats() {
      this.retryLoading = true;
      try {
        const params = { start_date: this.startDate, end_date: this.endDate };
        if (this.deliverId > 0) params.deliver_id = this.deliverId;
        const resp = await getRetryStats(params);
        if (resp.code === 0 && Array.isArray(resp.desc)) {
          this.retryStats = resp.desc.sort((a, b) => b.retry_count - a.retry_count);
        }
      } catch (err) {
        console.error("获取补偿统计失败:", err);
      } finally {
        this.retryLoading = false;
      }
    },
    async fetchDeviceSales() {
      this.salesLoading = true;
      try {
        const params = { start_date: this.startDate, end_date: this.endDate };
        if (this.deliverId > 0) params.deliver_id = this.deliverId;
        const resp = await getDeviceSalesStats(params);
        if (resp.code === 0) {
          if (Array.isArray(resp.desc)) {
            this.deviceSales = resp.desc;
          } else if (resp.desc && typeof resp.desc === "object") {
            this.deviceSales = [resp.desc];
          }
        }
      } catch (err) {
        console.error("获取设备销售失败:", err);
      } finally {
        this.salesLoading = false;
      }
    },
  },
};
</script>
